import * as React from "react";
import { Heading } from "@chakra-ui/react";

import {
  buildReportDetailMetaItems,
  ReportsCardMetaRowWithBadges,
} from "../Reports/components/ReportsCardChrome";
import { type ComparisonScopeSnapshot } from "./simulatorComparisonTypes";

function formatSimulationWindowLabel(scope: ComparisonScopeSnapshot): string {
  if (scope.localStartDate && scope.localEndDate) {
    return `${scope.localStartDate} → ${scope.localEndDate}`;
  }
  if (scope.useCustomRange && scope.startDate && scope.endDate) {
    return `${scope.startDate.slice(0, 10)} → ${scope.endDate.slice(0, 10)}`;
  }
  if (scope.timeframe?.trim()) return scope.timeframe.trim();
  return "All time";
}

function formatSimulationCreatedAt(createdAtMs: number): string | null {
  if (!Number.isFinite(createdAtMs) || createdAtMs <= 0) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(createdAtMs));
  } catch {
    return null;
  }
}

/** Same icon-chip meta row as report detail (account, window, created, candles, timezone). */
export function buildSimulationDetailMetaItems(options: {
  accountCode?: string | null;
  scope?: ComparisonScopeSnapshot | null;
  /** @deprecated Unused — report detail does not show entity IDs in the meta row. */
  simulationId?: string | null;
  createdAtMs?: number | null;
}): Array<{ id: string; node: React.ReactNode }> {
  const scope = options.scope ?? null;
  return buildReportDetailMetaItems({
    accountCode: options.accountCode?.trim() || null,
    dateLabel: scope ? formatSimulationWindowLabel(scope) : "—",
    createdAtDisplay:
      options.createdAtMs != null ? formatSimulationCreatedAt(options.createdAtMs) : null,
    candleTimeframe: scope?.candleTimeframe?.trim() || null,
    timezoneLabel: scope?.timezone?.trim() || null,
  });
}

export function SimulationDetailTitleCard({
  title,
  metaItems,
  strategyName,
  symbol,
}: {
  title: string;
  metaItems: Array<{ id: string; node: React.ReactNode }>;
  strategyName?: string | null;
  symbol?: string | null;
}): React.ReactElement {
  return (
    <div data-simulation-detail-title="" data-reports-card-chrome="" className="w-full min-w-0">
      <Heading
        as="h2"
        noOfLines={3}
        title={title}
        fontSize={{ base: "2xl", md: "3xl" }}
        color="#38BBD1"
        fontWeight="normal"
        fontFamily="'Urbanist', sans-serif"
        lineHeight="1.15"
        mt={0}
        mb={0}
        minW={0}
        maxW="100%"
      >
        {title}
      </Heading>
      {metaItems.length > 0 ? (
        <ReportsCardMetaRowWithBadges
          metaItems={metaItems}
          strategyName={strategyName}
          symbol={symbol}
        />
      ) : null}
    </div>
  );
}
